import {useEffect, useState} from 'react'

import PropTypes from 'prop-types'

import {filterWrapperBasicTypes} from '@adv-mt/mt-filter-wrapper-basic'
import FormCategory from '@adv-mt/mt-form-category'
import NotificationAi from '@adv-mt/mt-notifications-ai'
import SearchFilters, {searchFiltersButtonDesigns} from '@adv-mt/mt-search-filters'
import SearchSort, {searchSortButtonDesigns} from '@adv-mt/mt-search-sort'

import SearchAiInput from './components/searchAiInput/index.js'
import useAiSearch from './hooks/useAiSearch.js'
import {
  DEFAULT_SEARCH,
  SEARCH_AI_TOP_BAR_PLACES,
  SEARCH_AI_TOP_BAR_SEARCH_FILTERS_PLACES_DICTIONARY,
  SEARCH_AI_TOP_BAR_SEARCH_SORT_PLACES_DICTIONARY
} from './config.js'

const SearchAiTopBar = ({
  aggregations,
  categoryAggregations = [],
  dealerSlug,
  filtersConfig = [],
  filtersProps = {},
  isLoading: isLoadingFromProps = false,
  showCategorySearchBar = false,
  listingSectionId,
  onChangeCategory = () => {},
  onInputChange = () => {},
  onSearchBlur = () => {},
  onSearchFocus = () => {},
  isSearchFocused,
  onOpenNet,
  pageName,
  place,
  search,
  totalResults,
  withAiQuickFilters,
  showSearchSave = true
}) => {
  const [currentInputSearch, setCurrentInputSearch] = useState(DEFAULT_SEARCH)

  const {isLoading, notificationType, onCloseNotification, onSearch} = useAiSearch({
    isLoading: isLoadingFromProps,
    listingSectionId,
    // To remove when renting flow uses the same use case
    // Related tech-debt: https://jira.ets.mpi-internal.com/browse/MTR-94416
    pageName
  })

  const handleChangeInputSearch = search => {
    setCurrentInputSearch(search)
    onInputChange(search)
  }

  const handleInputSearch = () => {
    const cleanedInputSearch = currentInputSearch.trim()
    cleanedInputSearch && onSearch({query: cleanedInputSearch})
  }

  // SearchFilters lazy-loads its children, so it is kept out of SSR. It mounts after hydration:
  // a `typeof window` check would render it during the hydration pass too, and the markup
  // mismatch (React #418) makes React throw away the server HTML of the whole page.
  const [isMounted, setIsMounted] = useState(false)
  useEffect(() => setIsMounted(true), [])

  return (
    <>
      <div className="mt-SearchAiTopBar">
        {!showCategorySearchBar ? (
          <SearchAiInput
            isLoading={isLoading}
            listingSectionId={listingSectionId}
            onBlur={onSearchBlur}
            onChange={handleChangeInputSearch}
            onFocus={onSearchFocus}
            onSearch={handleInputSearch}
            search={currentInputSearch}
          />
        ) : (
          <div className="mt-SearchAiTopBar-category">
            <FormCategory
              aggregations={categoryAggregations}
              onChange={onChangeCategory}
              // TODO: create a config for Shape prop to FilterWrapperBasic: https://jira.ets.mpi-internal.com/browse/MTR-100229
              shape={'circular'}
              showLabel={false}
              value={search.categoryId || 2500}
              wrapper={filterWrapperBasicTypes.CENTER_TO_CUSTOM_RELATIVE_POPOVER_AUTOCLOSE}
            />
          </div>
        )}
        <SearchSort
          dealerSlug={dealerSlug}
          design={searchSortButtonDesigns.COMPRESSED}
          listingSectionId={listingSectionId}
          pageName={pageName}
          place={SEARCH_AI_TOP_BAR_SEARCH_SORT_PLACES_DICTIONARY[place]}
          search={search}
        />
        {isMounted && (
          <SearchFilters
            aggregations={aggregations}
            dealerSlug={dealerSlug}
            design={searchFiltersButtonDesigns.COMPRESSED}
            filtersConfig={filtersConfig}
            filtersProps={filtersProps}
            listingSectionId={listingSectionId}
            pageName={pageName}
            place={SEARCH_AI_TOP_BAR_SEARCH_FILTERS_PLACES_DICTIONARY[place]}
            search={search}
            totalResults={totalResults}
            withAiQuickFilters={withAiQuickFilters}
            showSearchSave={showSearchSave}
          />
        )}
      </div>
      {Boolean(notificationType) && !isLoading && (
        <NotificationAi onClose={onCloseNotification} type={notificationType} />
      )}
    </>
  )
}

SearchAiTopBar.displayName = 'SearchAiTopBar'
SearchAiTopBar.propTypes = {
  aggregations: PropTypes.array,
  categoryAggregations: PropTypes.array,
  dealerSlug: PropTypes.string,
  filtersConfig: PropTypes.array,
  filtersProps: PropTypes.object,
  isLoading: PropTypes.bool,
  showCategorySearchBar: PropTypes.bool,
  listingSectionId: PropTypes.string,
  onChangeCategory: PropTypes.func,
  onInputChange: PropTypes.func,
  onSearchBlur: PropTypes.func,
  onSearchFocus: PropTypes.func,
  isSearchFocused: PropTypes.bool,
  onOpenNet: PropTypes.func,
  pageName: PropTypes.string,
  place: PropTypes.string,
  search: PropTypes.object,
  totalResults: PropTypes.number,
  withAiQuickFilters: PropTypes.bool,
  showSearchSave: PropTypes.bool
}

export {SEARCH_AI_TOP_BAR_PLACES as searchAiTopBarPlaces}

export default SearchAiTopBar
